import React, { useContext, useEffect, useState } from 'react';
import { AuthContext } from '../../../context/AuthContext';
import api from '../../../services/api';
import DocumentsTab from '../../../components/Employee/DocumentsTab';

const Profile = () => {
  const { user } = useContext(AuthContext);
  const [profile, setProfile] = useState(null);
  const [photoUrl, setPhotoUrl] = useState(null);
  const [loadingPhoto, setLoadingPhoto] = useState(true);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let activeUrl = null;
    const fetchProfileData = async () => {
      try {
        const profileRes = await api.get(`/employees/${user.id}`);
        setProfile(profileRes.data);
        
        // Fetch Photo
        setLoadingPhoto(true);
        const docsRes = await api.get(`/employees/${user.id}/documents`);
        const photoItem = docsRes.data.find(item => item.type?.code === 'PHOTO' && item.document);
        if (photoItem) {
          const res = await api.get(`/employees/${user.id}/documents/${photoItem.document._id}/download`, { responseType: 'blob' });
          const url = window.URL.createObjectURL(new Blob([res.data], { type: res.headers['content-type'] }));
          activeUrl = url;
          setPhotoUrl(url);
        }
      } catch (error) {
        console.error('Failed to fetch profile data', error);
      } finally {
        setLoading(false);
        setLoadingPhoto(false);
      }
    };
    if (user?.id) {
      fetchProfileData();
    }
    return () => {
      if (activeUrl) window.URL.revokeObjectURL(activeUrl);
    };
  }, [user]);

  if (loading) return <div className="p-6">Loading profile...</div>;
  if (!profile) return <div className="p-6">Profile not found.</div>;

  return (
    <div className="max-w-4xl mx-auto p-6 mt-8 space-y-6">
      <div className="bg-white shadow overflow-hidden sm:rounded-lg">
        <div className="px-6 py-6 sm:px-8 flex flex-col sm:flex-row items-center sm:items-start gap-6 bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-gray-200">
          <div className="relative h-24 w-24 rounded-full border-4 border-white shadow-md bg-white overflow-hidden flex-shrink-0 flex items-center justify-center">
            {loadingPhoto ? (
              <div className="text-gray-400 text-xs">Loading...</div>
            ) : photoUrl ? (
              <img src={photoUrl} alt="Profile" className="h-full w-full object-cover" />
            ) : (
              <div className="h-full w-full bg-indigo-100 text-indigo-500 flex items-center justify-center text-3xl font-bold">
                {profile.firstName.charAt(0)}{profile.lastName.charAt(0)}
              </div>
            )}
          </div>
          <div className="flex-1 text-center sm:text-left mt-2 sm:mt-0">
            <h3 className="text-2xl font-bold text-gray-900">{profile.firstName} {profile.lastName}</h3>
            <p className="mt-1 text-sm text-gray-600 font-medium">{profile.designation || 'No Designation'} • {profile.department?.name || 'No Department'}</p>
          </div>
          <div className="mt-4 sm:mt-2">
            <span className="inline-flex items-center px-4 py-1.5 rounded-full text-sm font-semibold bg-blue-100 text-blue-800 shadow-sm border border-blue-200">
              {profile.role}
            </span>
          </div>
        </div>
        <div>
          <dl>
            <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Full name</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{profile.firstName} {profile.lastName}</dd>
            </div>
            <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Employee ID</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{profile.employeeId || 'Not assigned'}</dd>
            </div>
            <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Email address</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{profile.email}</dd>
            </div>
            <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Department</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{profile.department?.name || 'Not assigned'}</dd>
            </div>
            <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Designation</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{profile.designation || 'Not assigned'}</dd>
            </div>
          </dl>
        </div>
      </div>
      
      {/* Documents Section */}
      <DocumentsTab employeeId={user.id} />
    </div>
  );
};

export default Profile;
